/** The Lab's write tools that may return an `upload-intent` for screenshots. */
export const LAB_UPLOAD_TOOLS = new Set(['lab_file_report', 'lab_add_to_report']);
export const LAB_FILE_TOOL = 'lab_file_report';
export const LAB_MAX_SCREENSHOTS = 4;
/**
 * Does this message ask to report a bug / QA finding? (It then carries the report context, owner decision D8.)
 * Deliberately narrow: "issue" and "finding" alone are everyday construction words (an ACC issue, an inspection
 * finding), so only bug, defect, glitch, QA finding, test note, or "tell the devs it is broken" start a report.
 */
export function isBugReportRequest(text) {
    const t = text.toLowerCase();
    return /\b(report|file|log|raise|submit|open)\b[^.?!\n]{0,40}\b(bug|bugs|defect|qa finding|test note|glitch)\b/.test(t)
        || /\b(tell|let)\b[^.?!\n]{0,20}\b(devs?|developers?)\b[^.?!\n]{0,40}\b(broken|bug|wrong|not working|doesn'?t work)\b/.test(t)
        || /\bthis is (a )?bug\b/.test(t)
        || /\bqa finding\b/.test(t)
        || /\b(to|in|with) the lab\b/.test(t);
}
/** Turns in report mode without a Lab proposal before the widget stops sending the report context. */
export const REPORT_MODE_IDLE_TURNS = 6;
const FIELD_MAX = 300;
const CONTEXT_KEYS = ['route', 'pageTitle', 'appVersion', 'browser', 'os', 'process', 'windowTitle', 'host', 'agentVersion'];
function clean(value, max = FIELD_MAX) {
    if (typeof value !== 'string')
        return undefined;
    // One line, no control characters: this text is folded into model grounding (as fenced data).
    const flat = value.replace(/[\u0000-\u001f\u007f]+/g, ' ').replace(/\s+/g, ' ').trim();
    return flat ? flat.slice(0, max) : undefined;
}
/** Validate a host-supplied context (the desktop's `gyro:report-context`). Unknown fields are ignored. */
export function parseReportContext(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return null;
    const source = value;
    const out = {};
    for (const key of CONTEXT_KEYS) {
        const v = clean(source[key]);
        if (v)
            out[key] = v;
    }
    if (out.host && !/^[a-z0-9.-]{1,253}$/i.test(out.host))
        delete out.host;
    return Object.keys(out).length ? out : null;
}
/** The "Bug report context" block added to a report-mode turn's page context. The model reads it as data. */
export function formatReportContext(context, surface) {
    const c = context ?? {};
    const lines = [];
    if (surface === 'desktop') {
        lines.push('Bug report context (the window that was in front before Gyro opened on the desktop):');
        if (c.process)
            lines.push(`- Program: ${c.process}${c.browser ? ` (${c.browser})` : ''}`);
        if (c.windowTitle)
            lines.push(`- Window title: ${c.windowTitle}`);
        if (c.host)
            lines.push(`- Browser site: ${c.host}`);
        if (c.os)
            lines.push(`- OS: ${c.os}`);
        if (c.agentVersion)
            lines.push(`- Gyro desktop: ${c.agentVersion}`);
        if (lines.length === 1)
            lines.push('- (the desktop agent did not say which window was in front)');
    }
    else {
        lines.push('Bug report context (the page this Gyro widget is embedded in):');
        if (c.route)
            lines.push(`- Route: ${c.route}`);
        if (c.pageTitle)
            lines.push(`- Page title: ${c.pageTitle}`);
        if (c.appVersion)
            lines.push(`- App version: ${c.appVersion}`);
        if (c.browser)
            lines.push(`- Browser: ${c.browser}`);
        if (c.os)
            lines.push(`- OS: ${c.os}`);
    }
    return lines.join('\n');
}
/** Best-effort browser + OS names from a user-agent string (for the report's context line only). */
export function describeUserAgent(ua) {
    const browser = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : undefined;
    const os = /Windows NT 10/.test(ua) ? 'Windows 10/11' : /Windows/.test(ua) ? 'Windows' : /Mac OS X/.test(ua) ? 'macOS' : /Android/.test(ua) ? 'Android' : /(iPhone|iPad)/.test(ua) ? 'iOS' : /Linux/.test(ua) ? 'Linux' : undefined;
    return { ...(browser ? { browser } : {}), ...(os ? { os } : {}) };
}
const ATTACH_TYPES = new Set(['image/png', 'image/jpeg']);
const ATTACH_MAX_B64 = Math.ceil((5 * 1024 * 1024) / 3) * 4;
/** Validate a host's `gyro:attach` payload (the desktop's screen capture): up to 4 PNG/JPEG images. */
export function parseAttachImages(value) {
    if (!Array.isArray(value) || value.length < 1 || value.length > LAB_MAX_SCREENSHOTS)
        return null;
    const out = [];
    for (const item of value) {
        if (!item || typeof item !== 'object' || Array.isArray(item))
            return null;
        const { mediaType, data, name } = item;
        if (typeof mediaType !== 'string' || !ATTACH_TYPES.has(mediaType))
            return null;
        if (typeof data !== 'string' || data.length < 8 || data.length > ATTACH_MAX_B64 || !/^[A-Za-z0-9+/]+=*$/.test(data))
            return null;
        out.push({ kind: 'image', mediaType, data, name: clean(name, 200) ?? 'screenshot' });
    }
    return out;
}
/** The images that belong to a report: the last `count` images the person attached in this conversation. */
export function selectReportScreenshots(messages, count) {
    const wanted = Math.max(0, Math.min(LAB_MAX_SCREENSHOTS, Math.floor(Number(count) || 0)));
    if (!wanted)
        return [];
    const images = messages
        .filter((m) => m.role === 'user')
        .flatMap((m) => m.attachments ?? [])
        .filter((a) => a.kind === 'image');
    return images.slice(-wanted);
}
const INTAKE_PATH = /^\/api\/reports\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/evidence-intake$/i;
/** Parse the `upload-intent` handoff from /v1/action/result, or null when it is not one the widget may use. */
export function parseUploadIntent(value, now = Date.now()) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return null;
    const v = value;
    if (v.kind !== 'upload-intent' || v.singleUse !== true)
        return null;
    if (typeof v.token !== 'string' || !/^[A-Za-z0-9_-]{43}$/.test(v.token))
        return null;
    if (typeof v.url !== 'string')
        return null;
    let url;
    try {
        url = new URL(v.url);
    }
    catch {
        return null;
    }
    const loopback = url.protocol === 'http:' && (url.hostname === '127.0.0.1' || url.hostname === 'localhost');
    if ((url.protocol !== 'https:' && !loopback) || url.username || url.password || url.search || url.hash || !INTAKE_PATH.test(url.pathname))
        return null;
    const expiresAt = typeof v.expiresAt === 'string' ? Date.parse(v.expiresAt) : Number.NaN;
    if (!Number.isFinite(expiresAt) || expiresAt <= now)
        return null;
    const maxFiles = Number(v.maxFiles);
    const maxBytes = Number(v.maxBytes);
    if (!Number.isSafeInteger(maxFiles) || maxFiles < 1 || maxFiles > LAB_MAX_SCREENSHOTS)
        return null;
    if (!Number.isSafeInteger(maxBytes) || maxBytes < 1 || maxBytes > 4_000_000)
        return null;
    const accept = Array.isArray(v.accept) ? v.accept.filter((t) => typeof t === 'string' && ATTACH_TYPES.has(t)) : [];
    if (!accept.length)
        return null;
    return { url: url.toString(), token: v.token, expiresAt, maxFiles, maxBytes, accept };
}
/** Read the Lab's result ({reportId, shortId, url, status}); a link is kept only when it is a plain https URL. */
export function parseFiledReport(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return null;
    const v = value;
    if (typeof v.reportId !== 'string' || !/^[0-9a-f-]{8,64}$/i.test(v.reportId))
        return null;
    const shortId = typeof v.shortId === 'string' && /^[0-9a-z-]{4,40}$/i.test(v.shortId) ? v.shortId : v.reportId.slice(0, 8);
    let url;
    if (typeof v.url === 'string') {
        try {
            const parsed = new URL(v.url);
            if (parsed.protocol === 'https:' && !parsed.username && !parsed.password)
                url = parsed.toString();
        }
        catch {
            url = undefined;
        }
    }
    return { reportId: v.reportId, shortId, ...(url ? { url } : {}), ...(typeof v.status === 'string' ? { status: v.status.slice(0, 40) } : {}) };
}
/** Receipt line for a filed report (§1.6). `screenshots` describes the upload state. */
export function filedReceiptText(report, appId, screenshots) {
    const app = typeof appId === 'string' && appId ? appId : 'the app';
    const base = `Filed with **${app}** in The Lab — report \`${report.shortId}\``;
    switch (screenshots.state) {
        case 'none':
            return `${base}. It is waiting for the team to review it.`;
        case 'uploading':
            return `${base}. Attaching ${screenshots.count} screenshot${screenshots.count === 1 ? '' : 's'}…`;
        case 'attached':
            return `${base}, ${screenshots.count} screenshot${screenshots.count === 1 ? '' : 's'} attached. It is waiting for the team to review it.`;
        case 'failed':
            return `**Filed report \`${report.shortId}\` WITHOUT the screenshot:** ${screenshots.reason} Say *attach it again* to retry.`;
    }
}
/** Plain words for an upload that did not land (§4.5, last row). */
export function uploadFailureReason(status, error) {
    if (status === 413)
        return 'the image was too large for The Lab.';
    if (status === 415)
        return 'The Lab only accepts PNG or JPEG images.';
    if (status === 401 || status === 403 || status === 410)
        return 'the one-time upload link expired or was already used.';
    if (status === 0)
        return error ? `the upload could not be sent (${error}).` : 'the upload could not be sent (network error or blocked by this page).';
    return `The Lab answered ${status}${error ? ` (${error})` : ''}.`;
}
export function toDataUri(attachment) {
    return `data:${attachment.mediaType};base64,${attachment.data}`;
}
/** Browser upload: straight to The Lab with the one-time token in a header (never the URL), no cookies. */
export function fetchEvidenceUploader(fetchImpl = (...args) => fetch(...args), timeoutMs = 60_000) {
    return async (intent, dataUris) => {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), timeoutMs);
        try {
            const res = await fetchImpl(intent.url, {
                method: 'POST',
                mode: 'cors',
                credentials: 'omit',
                // The desktop uploader and gyro-svc's own receiver calls refuse redirects; so does this one (a stray 307 must
                // not re-POST the screenshots to another host).
                redirect: 'error',
                referrerPolicy: 'no-referrer',
                headers: { 'content-type': 'application/json', authorization: `LabIntake ${intent.token}` },
                body: JSON.stringify({ screenshots: dataUris }),
                signal: controller.signal,
            });
            if (res.ok)
                return { ok: true, status: res.status };
            let error;
            try {
                const body = (await res.json());
                if (typeof body.error === 'string')
                    error = body.error.slice(0, 200);
            }
            catch {
                /* a non-JSON refusal still has its status */
            }
            return { ok: false, status: res.status, ...(error ? { error } : {}) };
        }
        catch (e) {
            return { ok: false, status: 0, error: controller.signal.aborted ? 'timed out' : e instanceof Error ? e.message.slice(0, 200) : 'network error' };
        }
        finally {
            clearTimeout(timer);
        }
    };
}
/**
 * Desktop upload: the overlay's page may only talk to gyro-svc, so the widget asks the host (shell.html relays to
 * the native agent, which POSTs to the pinned Lab origin) and waits for the matching `gyro:lab-upload-result`.
 */
export function hostEvidenceUploader(win, timeoutMs = 90_000) {
    return (intent, dataUris) => new Promise((resolve) => {
        const requestId = `u-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
        let settled = false;
        const finish = (outcome) => {
            if (settled)
                return;
            settled = true;
            clearTimeout(timer);
            win.removeEventListener('message', onMessage);
            resolve(outcome);
        };
        const onMessage = (e) => {
            if (e.source !== win)
                return;
            const data = e.data;
            if (!data || typeof data !== 'object' || data.type !== 'gyro:lab-upload-result' || data.requestId !== requestId)
                return;
            const status = Number(data.status);
            finish({ ok: data.ok === true, status: Number.isSafeInteger(status) ? status : 0, ...(typeof data.error === 'string' ? { error: data.error.slice(0, 200) } : {}) });
        };
        const timer = setTimeout(() => finish({ ok: false, status: 0, error: 'the desktop agent did not answer' }), timeoutMs);
        win.addEventListener('message', onMessage);
        const target = win.location?.origin || '*';
        win.postMessage({ type: 'gyro:lab-upload', requestId, url: intent.url, token: intent.token, screenshots: dataUris }, target);
    });
}
/** Target size for a screenshot: the long edge at most `maxEdge`, aspect kept, never upscaled. */
export function scaledSize(width, height, maxEdge = 1600) {
    const w = Math.max(1, Math.round(width));
    const h = Math.max(1, Math.round(height));
    const long = Math.max(w, h);
    if (long <= maxEdge)
        return { width: w, height: h };
    const k = maxEdge / long;
    return { width: Math.max(1, Math.round(w * k)), height: Math.max(1, Math.round(h * k)) };
}
/** Decoded byte length of a base64 payload (no data: prefix). */
export function base64Bytes(data) {
    const padding = data.endsWith('==') ? 2 : data.endsWith('=') ? 1 : 0;
    return Math.floor((data.length * 3) / 4) - padding;
}
//# sourceMappingURL=bugReport.js.map